import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MapPin, Briefcase, Building2, Banknote, X } from "lucide-react";
import { toast } from "sonner";

const JobDetail = () => {
  const { id } = useParams();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", cover_letter: "", cv_url: "",
  });

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => {
      setJob(r.data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      setJob(null);
    });
  }, [id]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/jobs/${id}/apply`, {
        job_id: id,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        cover_letter: form.cover_letter || null,
        cv_url: form.cv_url || null,
      });
      toast.success(t("careers.applicationSuccess"));
      setShowApply(false);
      setForm({ full_name: "", email: "", phone: "", cover_letter: "", cv_url: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="max-w-[900px] mx-auto px-6 py-20">Loading...</div>;
  if (!job) return <div className="max-w-[900px] mx-auto px-6 py-20 text-center">Not found · <Link to="/careers" className="underline">Careers</Link></div>;

  const title = lang === "el" ? job.title_el : job.title_en;
  const description = lang === "el" ? job.description_el : job.description_en;
  const requirements = lang === "el" ? job.requirements_el : job.requirements_en;

  return (
    <div data-testid="job-detail-page" className="max-w-[900px] mx-auto px-6 lg:px-10 py-12">
      <button onClick={() => navigate("/careers")} className="text-sm hover:text-[#D4AF37] mb-8" data-testid="back-to-careers">
        {t("careers.backToList")}
      </button>
      <p className="overline mb-3">Amazypet · {job.department}</p>
      <h1 className="font-serif text-4xl lg:text-5xl mb-6">{title}</h1>

      <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-[#736B66] mb-10">
        <span className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> {job.location}</span>
        <span className="flex items-center gap-2"><Briefcase size={14} className="text-[#D4AF37]" /> {t(`careers.employment.${job.employment_type}`)}</span>
        {job.department && <span className="flex items-center gap-2"><Building2 size={14} className="text-[#D4AF37]" /> {job.department}</span>}
        {job.salary_range && <span className="flex items-center gap-2"><Banknote size={14} className="text-[#D4AF37]" /> {job.salary_range}</span>}
      </div>

      <section className="mb-10">
        <h2 className="font-serif text-2xl mb-4">{t("careers.description")}</h2>
        <p className="text-[#2C1E16] whitespace-pre-line leading-relaxed">{description}</p>
      </section>

      {requirements && (
        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-4">{t("careers.requirements")}</h2>
          <p className="text-[#2C1E16] whitespace-pre-line leading-relaxed">{requirements}</p>
        </section>
      )}

      <button onClick={() => setShowApply(true)} className="luxury-btn" data-testid="apply-now">
        {t("careers.applyNow")}
      </button>

      {showApply && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowApply(false)}>
          <div className="bg-white max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="apply-form">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-serif text-2xl">{t("careers.applyTitle")}</h3>
              <button onClick={() => setShowApply(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="overline mb-1 block">{t("careers.fullName")} *</label>
                <input required className="luxury-input-box" value={form.full_name} onChange={onChange("full_name")} data-testid="apply-name" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("careers.email")} *</label>
                <input type="email" required className="luxury-input-box" value={form.email} onChange={onChange("email")} data-testid="apply-email" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("careers.phone")}</label>
                <input className="luxury-input-box" value={form.phone} onChange={onChange("phone")} data-testid="apply-phone" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("careers.cvUrl")}</label>
                <input type="url" className="luxury-input-box" placeholder="https://..." value={form.cv_url} onChange={onChange("cv_url")} data-testid="apply-cv" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("careers.coverLetter")}</label>
                <textarea rows={5} className="luxury-input-box" value={form.cover_letter} onChange={onChange("cover_letter")} data-testid="apply-cover" />
              </div>
              <button type="submit" disabled={submitting} className="luxury-btn w-full" data-testid="apply-submit">
                {submitting ? "..." : t("careers.submitApplication")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
